// API Routes
export const API_ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    AUTHORIZE: '/auth/authorize',
    TOKEN: '/auth/token',
    REVOKE: '/auth/revoke',
    /** Credential update; Bearer-protected (same path when auth is a separate service). */
    PASSWORD: '/auth/password',
  },
  AUTH_SESSION: {
    EXCHANGE: '/auth/exchange',
    REFRESH: '/auth/refresh-session',
    LOGOUT: '/auth/logout-session',
  },
  CATEGORY: '/category',
  SALES: '/sales',
  SALES_V2: '/v2/sales',
  SALES_ORDER_V2: '/v2/sales-orders',
  PURCHASE_V2: '/v2/purchases',
  PURCHASE_ORDER_V2: '/v2/purchase-orders',
  EXPENSES: '/expenses',
  USER: {
    PROFILE: '/user/profile',
    PREFERENCES: '/user/preferences',
    PROFILE_PICTURE_UPLOAD_URL: '/user/profile-picture/upload-url',
    PROFILE_PICTURE: '/user/profile-picture',
    PROFILE_PICTURE_DOWNLOAD_URL: '/user/profile-picture/download-url',
  },
  COMPANY: '/company',
  COMPANY_MY_COMPANIES: '/company/my-companies',
  COMPANY_API_KEYS: '/company/api-keys',
  COMPANY_LOGO_UPLOAD_URL: '/company/:id/logo/upload-url',
  COMPANY_LOGO: '/company/:id/logo',
  COMPANY_LOGO_DOWNLOAD_URL: '/company/:id/logo/download-url',
  PRODUCT: '/products',
  PRODUCT_DRAFT: '/products/draft',
  INVENTORY: '/inventory',
  INVENTORY_HISTORY: '/inventory/history',
  INVENTORY_SOURCES: '/inventory-sources',
  PAYMENT_METHOD: '/payment-methods',
  DONATIONS: {
    CREATE: '/donations',
    CONFIRM: '/donations/confirm',
    STATUS: '/donations/:id/status',
  },
  CUSTOMERS: '/customers',
  SUPPLIERS: '/suppliers',
  MEMBERSHIPS: '/memberships',
  LANDING_SECTIONS: '/landing/sections',
  LANDING_ARTICLES: '/landing/articles',
  LANDING_CMS_SECTIONS: '/landing-cms/sections',
  LANDING_CMS_CONTENT: '/landing-cms/content',
  LANDING_CMS_ARTICLES: '/landing-cms/articles',
  BOARDS: '/boards',
  /** Socket.IO namespace for Kanban real-time updates (path segment only) */
  KANBAN_WS_NAMESPACE: '/kanban',
  STORAGE_PROVIDERS: '/storage-providers',
  FILES: '/files',
  FILES_UPLOAD_URL: '/files/upload-url',
  FILES_BATCH_DELETE: '/files/batch-delete',
  FILES_DOWNLOAD_ALL_URLS: '/files/download-all-urls',
  REPORTS: {
    HEADER_SETTINGS: '/reports/header-settings',
  },
  PERMISSION: '/permission',
  /** Chat (LLM) – streaming POST and conversation CRUD */
  ROLE: '/roles',
  /** Company-scoped cron schedules (`X-Company-Id` header). */
  SCHEDULES: '/schedules',
  CHAT: {
    STREAM: '/chat',
    CONVERSATIONS: '/chat/conversations',
    SUGGESTED_QUESTIONS: '/chat/suggested-questions',
  },
};

/** Active company for multi-tenant API calls; must match backend `x-company-id`. */
export const COMPANY_ID_HEADER = 'x-company-id' as const;

/** GET `/chat/conversations/:id/messages` */
export function chatConversationMessagesPath(conversationId: string): string {
  return `/chat/conversations/${encodeURIComponent(conversationId)}/messages`;
}

/** DELETE `/chat/conversations/:id` */
export function chatConversationPath(conversationId: string): string {
  return `/chat/conversations/${encodeURIComponent(conversationId)}`;
}

// UI Routes
export const ROUTES = {
  HOME: '/',
  /** Full-tab receipt image on hub origin; query `token` for API view URL. */
  RECEIPT_IMAGE_TAB: '/receipt-image',
  DASHBOARD: '/dashboard',
  AUTH_CALLBACK: '/auth/callback',
  REPORTS: {
    ROOT: '/reports',
    INVENTORY_REPORT: '/reports/inventory',
  },
  USER: {
    PROFILE: '/user/profile',
    CHANGE_PASSWORD: '/user/change-password',
    SETTINGS: '/user/settings',
    COMPLETE_PROFILE: '/user/complete-profile',
  },
  COMPANY: {
    SETUP: '/company/lobby',
    LOBBY: '/company/lobby',
    USERS: '/company/users',
  },
  COMPANIES: {
    ROOT: '/companies',
  },
  API_KEYS: '/api-keys',
  PRODUCTS: {
    ROOT: '/products',
    ADD: '/products/add',
    EDIT: '/products/:id/edit',
    INVENTORY: '/products/:id/inventory',
  },
  INVENTORY: {
    ROOT: '/inventory',
    PRODUCT: '/inventory/:id',
    ADD: '/inventory/add',
  },
  SALES: {
    ROOT: '/sales',
    ADD: '/sales/add',
    VIEW: '/sales/:id',
  },
  V2: {
    SALES_ORDERS: '/v2/sales-orders',
    SALES: '/v2/sales',
    PURCHASE_ORDERS: '/v2/purchase-orders',
    PURCHASES: '/v2/purchases',
  },
  EXPENSES: {
    ROOT: '/expenses',
    VIEW: '/expenses/:id',
  },
  CUSTOMERS: {
    ROOT: '/customers',
  },
  SUPPLIERS: {
    ROOT: '/suppliers',
  },
  PERMISSION: {
    ROOT: '/permission',
  },
  ROLE: {
    ROOT: '/roles',
  },
  POS: {
    ROOT: '/pos',
    PAYMENT: '/pos/payment',
    SUCCESS: '/pos/success',
    SETTINGS: '/pos/settings',
    PRINTER_SETUP: '/pos/printer-setup',
  },
  MEMBERSHIP: {
    ROOT: '/membership',
    LANDING: '/church/landingpage',
    LANDING_LEGACY: '/membership/landing',
    WEBSITE: '/membership/website',
    WEBSITE_SLUG: '/membership/website/:slug',
    WEBSITE_HOME: '/membership/home',
    WEBSITE_BUILDER: '/website-builder',
    WEBSITE_BUILDER_PAGES: '/website-builder/pages',
    WEBSITE_BUILDER_PAGE: '/website-builder/page/:pageId',
    SETTINGS: '/membership/settings',
    SETTINGS_CONTENT: '/membership/settings/content',
    SETTINGS_CONTENT_EDIT: '/membership/settings/content/edit/:slug',
    SETTINGS_SECTIONS: '/membership/settings/sections',
    ADD: '/membership/add',
    PENDING: '/membership/pending',
    STATISTICS: '/membership/statistics',
    HISTORY: '/membership/history',
    ROLLBACK: '/membership/rollback',
    VIEW: '/membership/:id',
    EDIT: '/membership/:id/edit',
    VIEW_HISTORY: '/membership/:id/history',
    VOID: '/membership/:id/void',
    RESTORE: '/membership/:id/restore',
    ROLLBACK_ONE: '/membership/:id/rollback',
  },
  CHURCH: {
    REPORTS: {
      ROOT: '/church',
      MEMBERS: '/church/members',
      DEPARTMENTAL: '/church/departmental',
      DEPARTMENTAL_DETAIL: '/church/departmental/:dept',
    },
  },
  KANBAN: '/kanban',
  STORAGE: {
    ROOT: '/storage',
  },
  SCHEDULES: {
    ROOT: '/schedules',
  },
  CHAT: '/chat',
  FORMS: '/forms',
  HELP: '/help',
  HELP_HOW_TO: '/help/how-to/:topic',
};

/** Public hub / marketing destination (e.g. website builder Hero “Get Started”, CTA “Sign Up”) */
export const ABRASOFT_HUB_URL = 'https://hub.abrasoft.com/';

/**
 * Vite bakes `VITE_*` at dev/build time; a bind-mounted core-frontend/.env with localhost
 * can win over Docker. When the app is opened via a LAN hostname/IP, align the API host with
 * the page so calls still hit the host-published API port.
 */
function alignApiUrlHostWithPage(fromEnv: string): string {
  if (!fromEnv) return '';
  if (typeof window === 'undefined') {
    return fromEnv;
  }
  const pageHost = window.location.hostname;
  const pageIsLoopback =
    pageHost === 'localhost' || pageHost === '127.0.0.1' || pageHost === '';
  if (pageIsLoopback) {
    return fromEnv;
  }
  try {
    const u = new URL(fromEnv.includes('://') ? fromEnv : `http://${fromEnv}`);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      u.hostname = pageHost;
      return u.toString().replace(/\/+$/, '');
    }
  } catch {
    /* ignore */
  }
  return fromEnv;
}

export function getReactAppApiUrl(): string {
  return alignApiUrlHostWithPage(
    (import.meta.env.VITE_API_URL || '').replace(/\/+$/, ''),
  );
}

export function getReactAppAuthUrl(): string {
  const raw =
    import.meta.env.VITE_AUTH_URL || import.meta.env.VITE_API_URL || '';
  return alignApiUrlHostWithPage(raw.replace(/\/+$/, ''));
}

// Authentication Constants
export const AUTH_CONSTANTS = {
  // Storage keys
  CODE_VERIFIER_KEY: 'oauth_code_verifier',
  STATE_KEY: 'oauth_state',
  RETURN_PATH_KEY: 'oauth_return_path',
  ACCESS_TOKEN_STORAGE_KEY: 'tkn',
  // Client configuration
  /** Keep in sync with backend AUTH_CLIENT_ID / docker-compose AUTH_CLIENT_ID. */
  CLIENT_ID: import.meta.env.VITE_CLIENT_ID || 'local-dev-client',
  /** Prefer getReactAppApiUrl() — getters support legacy AUTH_CONSTANTS.API_BASE_URL access. */
  get API_BASE_URL() {
    return getReactAppApiUrl();
  },
  /** OAuth authorize/login pages (may be a dedicated auth host). */
  get AUTH_BASE_URL() {
    return getReactAppAuthUrl();
  },
};

/** Full URL for Kanban Socket.IO namespace (server-to-client real-time updates) */
export function getKanbanSocketUrl(): string {
  const trimmed = getReactAppApiUrl();
  return trimmed ? `${trimmed}${API_ROUTES.KANBAN_WS_NAMESPACE}` : '';
}

export const CACHE_KEYS = {
  USER_PROFILE: 'user_profile_cache_v1',
  ACTIVE_COMPANY_ID_LEGACY: 'active_company_id_cache_v1',
  ACTIVE_COMPANY_ID: 'act_com',
  ACTIVE_COMPANY_ALIAS_MAP: 'act_com_alias_map',
};

export const URL_PARAM_KEYS = {
  COMPANY_ALIAS: 'co',
};

// Encryption Constants
export const ENCRYPTION_CONSTANTS = {
  APP_SALT:
    import.meta.env.VITE_ENCRYPTION_SALT || 'abrasoft-accounting-app-salt-2024',
  USER_SEED_KEY: 'user_encryption_seed',
};

// IndexedDB Constants
export const INDEXED_DB_CONSTANTS = {
  DB_NAME: 'abrasoft_auth_db',
  DB_VERSION: 1,
  STORE_NAME: 'tokens',
};

export const POS_ACCREDITATION_DETAILS = {
  PROVIDER_NAME: 'Abrasoft I.T. Services',
  PROVIDER_ADDRESS: '1 Example Street, Sample City',
  PROVIDER_TIN: '123-456-789-00001',
  PROVIDER_TIN_STATUS: 'Non-VAT Registered',
  ACCREDITATION_NUMBER: 'XXXXXXXX',
  ACCREDITATION_DATE_ISSUED: 'XX/XX/XXXX',
  ACCREDITATION_VALID_UNTIL: 'XX/XX/XXXX',
  PTU_NUMBER: 'Pending PTU assignment',
  PTU_DATE_ISSUED: 'XX/XX/XXXX',
} as const;

/**
 * Generic offline synchronization and caching utilities using IndexedDB
 */

export const OFFLINE_DB_NAME = 'abrasoft_offline_db';
export const OFFLINE_DB_VERSION = 2; // Incremented to fix open failures
export const PENDING_SYNC_STORE = 'pending_sync';
export const FAILED_SYNC_STORE = 'fss';
export const CACHE_STORE = 'cache';
